// @file For rendering Tldraw canvas in Vue component
import { isAppUsing } from '@@/bits/flip'
import { assetStore } from '@@/bits/tldraw/asset_store'
import Background from '@@/bits/tldraw/Background'
import EraserTool from '@@/bits/tldraw/EraserTool'
import ErrorFallback from '@@/bits/tldraw/ErrorFallback'
import InteractTool from '@@/bits/tldraw/InteractTool'
import OnTheCanvas from '@@/bits/tldraw/OnTheCanvas'
import PadletAttachmentShapeUtil from '@@/bits/tldraw/PadletAttachmentShape'
import PadletHalfEllipseShapeUtil from '@@/bits/tldraw/PadletHalfEllipseShape'
import PadletJamboardDrawGroupShapeUtil from '@@/bits/tldraw/PadletJamboardDrawGroupShape'
import { PadletSpeechBubbleTool } from '@@/bits/tldraw/PadletSpeechBubbleTool'
import { PadletSpeechBubbleUtil } from '@@/bits/tldraw/PadletSpeechBubbleUtil'
import { ShapeIndicator } from '@@/bits/tldraw/ShapeIndicator'
import { FloatingToolbar } from '@@/bits/tldraw/tiptap_extension_floating_toolbar'
import { TldrawHandles } from '@@/bits/tldraw/TldrawHandles'
import { SetupHooks } from '@@/bits/tldraw/tldraw_hooks'
import { createBridgeToReactState, type BridgedStateHook } from '@@/bits/vue_react_bridge'
import { MAX_FRAME_LIMIT } from '@@/bits/whiteboard'
import { ShownCursorType } from '@@/enums'
import { useWhiteboardCollaborationStore } from '@@/pinia/whiteboard_collaboration'
import { useWhiteboardModeStore } from '@@/pinia/whiteboard_mode'
import { useWhiteboardPermissionsStore } from '@@/pinia/whiteboard_permissions'
import { useTldrawCollaboratorComponents } from '@@/vuecomposables/useTldrawCollaboratorComponents'
import { useWhiteboardConnectionUri } from '@@/vuecomposables/useWhiteboardConnectionUri'
import { useWhiteboardElementTextContentModifier } from '@@/vuecomposables/use_whiteboard_element_text_content_modifier'
import { defaultExtensions } from '@padlet/whiteboard-tiptap'
import { useSync } from '@tldraw/sync'
import { storeToRefs } from 'pinia'
import { useCallback, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import type { TLComponents, TLRecord, TLShapeId, TLTextOptions } from 'tldraw'
import {
  defaultBindingUtils,
  DefaultHandle,
  DefaultShapeIndicators,
  defaultShapeUtils,
  Editor,
  getDefaultUserPresence,
  NoteShapeUtil,
  Tldraw,
} from 'tldraw'
import { computed, onMounted } from 'vue'

/**
 * Whitelisted domains:
 * - padlet.com
 * - padlet.dev
 * - *.padletorg.dev
 * - *.padlet.org
 * - *.wallwisher.net
 */
const TLDRAW_LICENSE_KEY =
  'tldraw-padlet--wallwisher-inc--2025-05-31/WyIzMWJ2OEF4MSIsWyIqLnBhZGxldC5jb20iLCIqLnBhZGxldC5vcmciLCIqLnBhZGxldC5kZXYiLCIqLnBhZGxldG9yZy5kZXYiLCIqLndhbGx3aXNoZXIubmV0Il0sMSwiMjAyNS0wNS0zMSJd.znJDtVhsQzUmaR+TjvlxNegniiZwOv+6Q+LXztWXe1RStbdYymzJ+S055ZrZ8uc3r3/T9XmhnpeHxR239yrDxg'

// NOTE: These constants must always be static. If we put them inside DrawingCanvas,
// their references become unstable and Tldraw will rerender the whole canvas.
const CUSTOM_SHAPE_UTILS = [
  NoteShapeUtil.configure({ resizeMode: isAppUsing('sandboxResizableNotes') ? 'scale' : 'none' }),
  PadletAttachmentShapeUtil,
  PadletHalfEllipseShapeUtil,
  PadletJamboardDrawGroupShapeUtil,
  PadletSpeechBubbleUtil,
]

const CUSTOM_AND_DEFAULT_SHAPE_UTILS = [
  // Exclude NoteShapeUtil from the default list because we customize it
  // in CUSTOM_SHAPE_UTILS
  ...defaultShapeUtils.filter((shapeUtil) => shapeUtil !== NoteShapeUtil),
  ...CUSTOM_SHAPE_UTILS,
]

const CUSTOM_TOOLS = [EraserTool, InteractTool, PadletSpeechBubbleTool]

const TLDRAW_COMPONENTS: TLComponents = {
  ShapeIndicator,
  ShapeIndicators: DefaultShapeIndicators, // TLdrawShapeIndicators has logic that we don't need https://github.com/tldraw/tldraw/pull/4083/files, we render DefaultShapeIndicators directly
  Handles: TldrawHandles,
  Handle: DefaultHandle,
  ContextMenu: null,
  Background,
  // OnTheCanvas ensures the component will scale with the camera zoom, while also not being affected by the tools
  OnTheCanvas,
  ErrorFallback,
  ...useTldrawCollaboratorComponents(),
}

const TEXT_OPTIONS: Partial<TLTextOptions> = {
  tipTapConfig: {
    extensions: [
      ...defaultExtensions,
      // FloatingToolbar is used to display a toolbar of text formatting options (WhiteboardElementTextContentModifier) when text is selected
      FloatingToolbar.configure({
        getElement: useWhiteboardElementTextContentModifier().getElement,
        onShow: useWhiteboardElementTextContentModifier().showMenu,
        onHide: useWhiteboardElementTextContentModifier().hideMenu,
      }),
    ] as any,
  },
}

const ASSET_URLS = {
  fonts: {
    // Courier Prime
    tldraw_mono: 'https://padlet.net/fonts/courierprime/courier_prime-webfont.woff2',
    tldraw_mono_italic: 'https://padlet.net/fonts/courierprime/courier_prime_italic-webfont.woff2',
    tldraw_mono_bold: 'https://padlet.net/fonts/courierprime/courier_prime_bold-webfont.woff2',
    tldraw_mono_italic_bold: 'https://padlet.net/fonts/courierprime/courier_prime_bold_italic-webfont.woff2',
    // Lora
    tldraw_serif: 'https://padlet.net/fonts/lora/3.006/Lora-Medium.woff2',
    tldraw_serif_italic: 'https://padlet.net/fonts/lora/3.006/Lora-MediumItalic.woff2',
    tldraw_serif_bold: 'https://padlet.net/fonts/lora/3.006/Lora-Bold.woff2',
    tldraw_serif_italic_bold: 'https://padlet.net/fonts/lora/3.006/Lora-BoldItalic.woff2',
    // Inter
    tldraw_sans: 'https://padlet.net/fonts/inter/3.19/Inter-Medium.woff2',
    tldraw_sans_italic: 'https://padlet.net/fonts/inter/3.19/Inter-MediumItalic.woff2',
    tldraw_sans_bold: 'https://padlet.net/fonts/inter/3.19/Inter-Bold.woff2',
    tldraw_sans_italic_bold: 'https://padlet.net/fonts/inter/3.19/Inter-BoldItalic.woff2',
    // Shantell Sans
    tldraw_draw: 'https://padlet.net/fonts/shantell-sans/Shantell_Sans-Normal-Medium.woff2',
    tldraw_draw_italic: 'https://padlet.net/fonts/shantell-sans/Shantell_Sans-Normal-Medium_Italic.woff2',
    tldraw_draw_bold: 'https://padlet.net/fonts/shantell-sans/Shantell_Sans-Normal-Bold.woff2',
    tldraw_draw_bold_italic: 'https://padlet.net/fonts/shantell-sans/Shantell_Sans-Normal-Bold_Italic.woff2',
  },
}

const DrawingCanvas = ({
  onMount,
  canIViewShape,
  useShownCursorType,
  getConnectionUri,
}: {
  onMount: (editor: Editor) => void
  canIViewShape: (record: TLRecord) => boolean
  useShownCursorType: BridgedStateHook<ShownCursorType>
  getConnectionUri: () => Promise<string>
}): React.JSX.Element => {
  const [shownCursorType] = useShownCursorType()

  const getUserPresence = useCallback<typeof getDefaultUserPresence>(
    (store, user) => {
      if (shownCursorType === ShownCursorType.NONE) return null
      return getDefaultUserPresence(store, user)
    },
    [getDefaultUserPresence, shownCursorType],
  )

  const store = useSync({
    // The uri callback is a function instead of a string to ensure the access token
    // is retrieved on-demand rather than at a fixed time, allowing us to generate a fresh token
    // each time we try to connect to the whiteboard.
    uri: getConnectionUri,
    assets: assetStore,
    shapeUtils: CUSTOM_AND_DEFAULT_SHAPE_UTILS,
    bindingUtils: defaultBindingUtils,
    getUserPresence,
  })

  // This function handles hiding unapproved shapes from those who shouldn't be able to see it
  // because as of v2.4, Tldraw does not have any built-in method to control shape rendering.
  useEffect(() => {
    /**
     * DISABLE UNAPPROVED SHAPE SELECTION OUTLINE (BY COLLABORATORS)
     */

    const _superGetCollaborators = Editor.prototype.getCollaborators
    Editor.prototype.getCollaborators = function () {
      const collaborators = _superGetCollaborators.call(this)
      return collaborators.map((c) => ({
        ...c,
        selectedShapeIds: c.selectedShapeIds.filter((id) => {
          const shape = this.getShape(id)
          return shape != null && canIViewShape(shape)
        }),
      }))
    }

    /**
     * DISABLE UNAPPROVED SHAPE RENDERING
     */

    const _superGetCurrentPageShapeIds = Editor.prototype.getCurrentPageShapeIds
    Editor.prototype.getCurrentPageShapeIds = function () {
      const ids = _superGetCurrentPageShapeIds.call(this)
      return new Set(
        [...ids].filter((id) => {
          const shape = this.getShape(id)
          return shape != null && canIViewShape(shape)
        }),
      )
    }

    const _superGetSortedChildIdsForParent = Editor.prototype.getSortedChildIdsForParent
    Editor.prototype.getSortedChildIdsForParent = function (parentId: TLShapeId) {
      const ids = _superGetSortedChildIdsForParent.call(this, parentId)
      return ids.filter((id) => {
        const shape = this.getShape(id)
        return shape != null && canIViewShape(shape)
      })
    }
  }, [canIViewShape])

  return (
    <Tldraw
      licenseKey={TLDRAW_LICENSE_KEY}
      shapeUtils={CUSTOM_SHAPE_UTILS}
      onMount={onMount}
      store={store}
      tools={CUSTOM_TOOLS}
      components={TLDRAW_COMPONENTS}
      textOptions={TEXT_OPTIONS}
      assetUrls={ASSET_URLS}
      options={{
        // Tldraw default is 40
        maxPages: MAX_FRAME_LIMIT,
      }}
      hideUi
    >
      {/*
       * Most of the hooks that Tldraw provides require the editor to be loaded.
       * Since hooks can't be called conditionally, and we use Vue everywhere else,
       * the easiest way to call Tldraw hooks is to call them in <Tldraw /> children.
       */}
      <SetupHooks />
    </Tldraw>
  )
}

export const useTldrawCanvas = ({
  elementId,
  onMount,
}: {
  elementId: string
  onMount: (editor: Editor) => void
}): void => {
  const whiteboardPermissionsStore = useWhiteboardPermissionsStore()
  const { isReadOnlyMode } = storeToRefs(useWhiteboardModeStore())
  const { shownCursorType } = storeToRefs(useWhiteboardCollaborationStore())
  const { getConnectionUri } = useWhiteboardConnectionUri()

  const bridgedShownCursorType = createBridgeToReactState(
    computed<ShownCursorType>(() => {
      if (isReadOnlyMode.value) return ShownCursorType.NONE
      return shownCursorType.value
    }),
  )

  onMounted(() => {
    if (elementId == null) {
      throw new Error('Element id is required')
    }
    const domNode = document.getElementById(elementId) as HTMLElement
    if (domNode == null) {
      throw new Error(`Element with id ${elementId} not found`)
    }
    const root = createRoot(domNode)
    root.render(
      <DrawingCanvas
        onMount={onMount}
        canIViewShape={whiteboardPermissionsStore.canIViewShape}
        useShownCursorType={bridgedShownCursorType}
        getConnectionUri={getConnectionUri}
      />,
    )
  })
}
